import { toast } from 'react-toastify';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import header from '../../../assets/icm-header.jpg';
import {
  getBase64PDFImage,
  scaleStyles,
  baseStyles,
  getPrefix,
} from '../../../util/utils';
import {
  getPdfBase64,
  enviarArquivo,
  normalizeWhatsappPhone,
} from '../../../services/pdfWhatsapp';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

function buildListSection(title, items = [], field = 'DESCRICAO') {
  if (!Array.isArray(items) || items.length === 0) return [];

  const rows = items
    .map(item => item && item[field])
    .filter(Boolean)
    .map(texto => ({
      text: texto,
      style: 'content',
      color: '#444',
      margin: [0, 1, 0, 1],
    }));

  if (rows.length === 0) return [];

  return [
    {
      text: title,
      bold: true,
      style: 'content',
      margin: [0, 6, 0, 2],
    },
    ...rows,
  ];
}

function buildTextSection(title, value) {
  if (!value) return [];

  return [
    {
      text: title,
      bold: true,
      style: 'content',
      margin: [0, 6, 0, 2],
    },
    {
      text: String(value),
      style: 'content',
      color: '#444',
      margin: [0, 1, 0, 1],
    },
  ];
}

export default async function generateErgoPDF(
  ergo,
  tipo,
  fontScale = 0.9,
  pageBreaks = {}
) {
  const {
    pageBreakA = false,
    pageBreakB = false,
    pageBreakC = false,
    cpfPaciente = false,
  } = pageBreaks || {};
  const styles = scaleStyles(baseStyles, fontScale);
  const headerImage =
    tipo === 'imprimirTimbrado' || tipo === 'envioWpp'
      ? await getBase64PDFImage(header)
      : null;

  const velColumnHeader =
    ergo?.PROTOCOLO === 'Rampa' ? 'Vel(Km/h)' : 'Vel(mph)';

  const estagiosBody = [
    [
      { text: 'Estágio', bold: true, style: 'smaller' },
      { text: 'Tempo(min)', bold: true, style: 'smaller' },
      { text: velColumnHeader, bold: true, style: 'smaller' },
      { text: 'Inc(%)', bold: true, style: 'smaller' },
      { text: 'FC(bpm)', bold: true, style: 'smaller' },
      { text: 'PA(mmHg)', bold: true, style: 'smaller' },
      { text: 'Sinais/Sintomas', bold: true, style: 'smaller' },
      { text: 'Arritmias', bold: true, style: 'smaller' },
    ],
    ...(ergo?.estagios || []).map(estagio => [
      { text: String(estagio.CODESTAGIO ?? ''), style: 'smaller' },
      { text: String(estagio.TEMPO ?? ''), style: 'smaller' },
      { text: String(estagio.VELOCIDADE ?? ''), style: 'smaller' },
      { text: String(estagio.INCLINACAO ?? ''), style: 'smaller' },
      { text: String(estagio.FC ?? ''), style: 'smaller' },
      { text: String(estagio.PA ?? ''), style: 'smaller' },
      { text: String(estagio.Sintomas ?? ''), style: 'smaller' },
      { text: String(estagio.Arritmias ?? ''), style: 'smaller' },
    ]),
  ];

  const recuperacoesBody = [
    [
      { text: 'Recuperação', bold: true, style: 'smaller' },
      { text: 'Tempo(min)', bold: true, style: 'smaller' },
      { text: 'FC(bpm)', bold: true, style: 'smaller' },
      { text: 'PA(mmHg)', bold: true, style: 'smaller' },
      { text: 'Sinais/Sintomas', bold: true, style: 'smaller' },
      { text: 'Arritmias', bold: true, style: 'smaller' },
    ],
    ...(ergo?.recuperacoes || []).map(recup => [
      { text: String(recup.CODRECUPERACAO ?? ''), style: 'smaller' },
      { text: String(recup.TEMPO ?? ''), style: 'smaller' },
      { text: String(recup.FC ?? ''), style: 'smaller' },
      { text: String(recup.PA ?? ''), style: 'smaller' },
      { text: String(recup.Sintomas ?? ''), style: 'smaller' },
      { text: String(recup.Arritmias ?? ''), style: 'smaller' },
    ]),
  ];

  const assinaturaDigital = ergo?.HASH || md5(JSON.stringify(ergo || {}));

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: headerImage ? [40, 130, 40, 60] : [40, 80, 40, 60],
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'center',
          style: 'footer',
        },
      ],
      margin: [0, 10, 0, 0],
    }),
    content: [
      {
        text: 'TESTE ERGOMÉTRICO',
        style: 'title',
        alignment: 'center',
        margin: [0, 0, 0, 5],
      },
      // --- Cabeçalho com retângulo preto ---
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    columns: [
                      {
                        text: `Nome do Paciente: ${ergo?.CODPAC || ''} - ${
                          ergo?.NOMEPAC || ''
                        }${
                          cpfPaciente && ergo?.CPFPAC
                            ? ` - CPF: ${ergo.CPFPAC}`
                            : ''
                        }`,
                        width: '100%',
                        bold: true,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Data Nasc: ${ergo?.dataNasc || ''}`,
                        width: '50%',
                      },
                      {
                        text: `Sexo: ${ergo?.SEXOPAC || ''}`,
                        width: '20%',
                      },
                      {
                        text: `Idade: ${ergo?.idadePAC ?? ''} anos`,
                        width: '30%',
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Convênio: ${ergo?.SIGLACONV || ''}`,
                        width: '50%',
                      },
                      {
                        text: `Código: ${ergo?.CODEXAME || ''}`,
                        width: '20%',
                      },
                      {
                        text: `Data Laudo: ${ergo?.dataErgo || ''}`,
                        width: '30%',
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Médico que Solicitou: ${getPrefix(
                          ergo?.CODSOLICITANTE
                        )} ${ergo?.Solicitou || ''}`,
                        width: '100%',
                      },
                    ],
                  },
                ],
                margin: [10, 2, 10, 2],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => 'black',
          vLineColor: () => 'black',
        },
        style: 'content',
        margin: [0, 0, 0, 8],
      },

      // --- Seções Clínicas e Avaliações ---
      ...buildListSection('Indicações do Teste', ergo?.indicacoes, 'DESCRICAO'),
      ...buildListSection('Queixas', ergo?.queixas, 'FRASE'),
      ...buildListSection('Medicamentos', ergo?.medicamentos, 'FRASE'),
      ...buildListSection('Fatores de Risco', ergo?.fatoresRisco, 'DESCRICAO'),
      ...buildListSection(
        'Atividade Física',
        ergo?.atividadesFisica,
        'DESCRICAO'
      ),
      ...buildTextSection('Exame Físico', ergo?.EXAMEFISICO),
      ...buildListSection('Eletrocardiograma Basal', ergo?.ecgBasal, 'FRASE'),

      // --- Frequência Cardíaca Prevista ---
      {
        text: 'Frequência Cardíaca Prevista',
        bold: true,
        style: 'content',
        margin: [0, 8, 0, 4],
        ...(pageBreakA ? { pageBreak: 'before' } : {}),
      },
      {
        table: {
          widths: ['*', '*'],
          body: [
            [
              {
                text: `Máxima (FC): ${ergo?.FREQMAX ?? ''} bpm`,
                style: 'content',
              },
              {
                text: `Submáxima (FC): ${ergo?.FREQSUB ?? ''} bpm`,
                style: 'content',
              },
            ],
          ],
        },
        margin: [0, 0, 0, 6],
      },

      // --- Frequência Cardíaca e Pressão Arterial ---
      {
        text: 'Frequência Cardíaca e Pressão Arterial',
        bold: true,
        style: 'content',
        margin: [0, 6, 0, 4],
      },
      {
        table: {
          widths: ['*', '*'],
          body: [
            [
              {
                text: `Deitado (FC): ${ergo?.FCDEITADO ?? ''} bpm`,
                style: 'content',
              },
              {
                text: `PA: ${ergo?.PADEITADO ?? ''} mmHg`,
                style: 'content',
              },
            ],
            [
              {
                text: `Em Pé (FC): ${ergo?.FCPE ?? ''} bpm`,
                style: 'content',
              },
              {
                text: `PA: ${ergo?.PAPE ?? ''} mmHg`,
                style: 'content',
              },
            ],
          ],
        },
        margin: [0, 0, 0, 6],
      },

      // --- Protocolo, Estágios e Recuperação ---
      {
        text: `Protocolo de ${ergo?.PROTOCOLO || ''}`,
        bold: true,
        style: 'content',
        margin: [0, 6, 0, 4],
        ...(pageBreakB ? { pageBreak: 'before' } : {}),
      },
      {
        text: 'Estágio',
        bold: true,
        style: 'content',
        margin: [0, 2, 0, 2],
      },
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', '*', '*'],
          body: estagiosBody,
        },
        margin: [0, 0, 0, 6],
      },
      {
        text: 'Recuperação',
        bold: true,
        style: 'content',
        margin: [0, 4, 0, 2],
      },
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', 'auto', 'auto', '*', '*'],
          body: recuperacoesBody,
        },
        margin: [0, 0, 0, 6],
      },

      // --- Alterações Eletrocardiográficas e Conclusões ---
      ...(() => {
        const postStageSections = [
          ...buildListSection(
            'Alterações Eletrocardiográficas',
            ergo?.altECG,
            'DESCRICAO'
          ),
          ...buildListSection('Arritmias', ergo?.arritmias, 'DESCRICAO'),
          ...buildListSection(
            'Comportamento da PA',
            ergo?.comportamentoPA,
            'DESCRICAO'
          ),
          ...buildListSection('Comentários', ergo?.comentarios, 'DESCRICAO'),
          ...buildListSection('Conclusões', ergo?.conclusoes, 'DESCRICAO'),
        ];

        if (pageBreakC && postStageSections.length > 0) {
          postStageSections[0] = {
            ...postStageSections[0],
            pageBreak: 'before',
          };
        }

        return postStageSections;
      })(),

      // --- Nota Legal ---
      {
        text: 'O Resultado do presente laudo não configura necessariamente a presença ou ausência de doença, devendo obrigatoriamente ser correlacionado com demais dados clínicos e exames complementares pertinentes ao caso.',
        style: 'smaller',
        italics: true,
        alignment: 'center',
        margin: [0, 12, 0, 6],
      },

      // --- Aviso de Imagens ---
      {
        text: `Imagens disponíveis no site: www.icm.com.br (Resultado de Exames)\nCódigo de verificação: ${
          ergo?.CODPAC || ''
        }`,
        style: 'smaller',
        italics: true,
        alignment: 'left',
        margin: [0, 0, 0, 6],
      },

      // --- Assinatura Eletrônica ---
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    text: `Conferido e assinado eletronicamente por: ${getPrefix(
                      ergo?.CODREALIZOU
                    )} ${ergo?.Realizou || ''} - CRM: ${
                      ergo?.CRMMED || ''
                    } - RQE: ${ergo?.RQE || ''}`,
                    bold: true,
                    italics: true,
                    style: 'smaller',
                    alignment: 'center',
                  },
                  {
                    text: `Assinatura Digital: ${assinaturaDigital}`,
                    italics: true,
                    style: 'smaller',
                    alignment: 'center',
                  },
                  {
                    text: [
                      { text: 'Emitido em: ', bold: true },
                      `${new Date().toLocaleString('pt-BR')}\n`,
                    ],
                    style: 'smaller',
                    italics: true,
                    color: '#444',
                    alignment: 'center',
                  },
                ],
                style: 'content',
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: '#999',
          vLineColor: '#999',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 1, 0, 1],
      },
    ],
    styles,
  };

  if (headerImage) {
    docDefinition.header = {
      image: headerImage,
      width: 500,
      alignment: 'center',
      margin: [0, 10, 0, 0],
    };
  }

  if (tipo === 'imprimirNormal' || tipo === 'imprimirTimbrado') {
    pdfMake.createPdf(docDefinition).open();
  } else {
    const celular = normalizeWhatsappPhone(ergo?.CELULARPAC);
    if (!celular) {
      toast.warn('Número de celular não encontrado!');
      return;
    }

    try {
      const pdfGenerator = pdfMake.createPdf(docDefinition);
      const pdfBase64 = await getPdfBase64(pdfGenerator);
      const data = await enviarArquivo({
        to: celular,
        pdfBase64,
        paciente: {
          id: ergo?.CODPAC,
          nome: ergo?.NOMEPAC,
          medico: ergo?.Realizou,
        },
        codLaudo: ergo?.CODEXAME,
        fileName: `Laudo_Ergo_${ergo?.CODEXAME}.pdf`,
        caption: `Olá *${ergo?.NOMEPAC}*, tudo bem? 😊

Segue em anexo o seu *Laudo de Teste Ergométrico*.

As *imagens do exame* podem ser acessadas pelo link:
👉 http://www.icm.com.br (Resultados de Exames)

Qualquer dúvida, nossa equipe estará à disposição no setor de atendimento pelo WhatsApp 14-3402-5252\n
ICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'ergo',
          documentTitle: 'Laudo de Teste Ergométrico',
        },
      });
      toast.success(data.message);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Erro ao enviar laudo via WhatsApp';
      toast.error(errorMessage);
    }
  }

  return docDefinition;
}
